import { showNotification } from "#/src/utils/notification";
import {
  DefaultOptions,
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
  QueryClientConfig,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { isApiResponseError } from "@web-monorepo/fetchers";
import { clearSessionQueryData } from "./hooks/useSession";
import { checkIsCoppaError } from "#/src/hooks/api/parent/checkIsCoppaError";
import { setLoginRedirectIfUnset } from "#/src/utils/loginRedirect";
import * as logClient from "@classdojo/log-client";
import { DEFAULT_ERROR_MESSAGE, getApiErrorBody, isNetworkFailure } from "#/src/utils/apiError";
import { reportFetcherNetworkFailure } from "#/src/utils/networkFailure";

// `detail` is typed string (ApiErrorBody) but a few endpoints send `{ error }`.
const constructMessage = (detail: unknown) => {
  if (typeof detail === "string" && detail) return detail;
  if (typeof detail === "object" && detail !== null && "error" in detail && typeof detail.error === "string") {
    return detail.error;
  }
  return DEFAULT_ERROR_MESSAGE;
};

type HandleErrorOptions = {
  ignoreExpected: boolean;
  notificationDetails: { title?: string; message?: string } | undefined;
  notify: typeof showNotification;
};

// Split out of `handleError` to keep it under the eslint complexity limit. Callers have already
// screened out network failures and errors without a response.
const handleApiResponseError = (error: unknown, options: HandleErrorOptions) => {
  const errorData = getApiErrorBody(error);

  if (options.ignoreExpected && errorData?.expected) {
    return;
  }

  // if we get a 401 we should redirect to the login page
  if (errorData?.type === 401) {
    clearSessionQueryData();
    setLoginRedirectIfUnset();
    window.location.href = "#/login";
    return;
  }

  // we shouldn't show notification on COPPA error since we should show the COPPA consent modal instead
  if (checkIsCoppaError(error)) {
    return;
  }

  options.notify({
    title: options.notificationDetails?.title ?? errorData?.message ?? "Error",
    message: options.notificationDetails?.message ?? constructMessage(errorData?.detail),
    color: "red",
  });
};

export const handleError = (
  ignoreExpected = false,
  notificationDetails?: { title?: string; message?: string },
  // Test seam: vitest.setup imports this module before #/test/library mocks notification. Production
  // code that wants no notification uses handleErrorWithoutNotification instead.
  notify: typeof showNotification = showNotification,
) => {
  return (error: unknown) => {
    // Network failures (see isNetworkFailure) are reported by the transport layer, the axios
    // errorHandler or reportFetcherNetworkFailure, unless the request opted out.
    if (isNetworkFailure(error)) return;

    // Anything else that isn't an API failure leaking into a query/mutation onError handler is
    // unexpected: log and bail.
    if (!isAxiosError(error) && !isApiResponseError(error)) {
      logClient.logMessage("WARN", "tutor-non-api-error-in-handle-error", {
        site: "marketplace",
        errorString: String(error),
      });
      return;
    }

    // This can be removed when we stop getting errors without a response here.
    // If we continue to see this, it's likely we want to add a code to the
    // NETWORK_ERROR_CODES set in apiError.
    // See: https://app.datadoghq.com/logs?query=%40site%3Amarketplace%20%40log_type%3Aerror%20%22frontend_site.error%22%20%40context.type%3Atutor-axios-error-without-response&agg_m=count&agg_m_source=base&agg_q=status%2Cservice&agg_q_source=base%2Cbase&agg_t=count&clustering_pattern_field_path=message&cols=core_host%2Ccore_service&messageDisplay=inline&refresh_mode=sliding&saved-view-id=1923652&sort_m=%2C&sort_m_source=%2C&sort_t=%2C&storage=hot&stream_sort=desc&top_n=10%2C10&top_o=top%2Ctop&viz=stream&x_missing=true%2Ctrue&from_ts=1755016821390&to_ts=1755103221390&live=true
    if (isAxiosError(error) && !error.response) {
      logClient.logMessage("WARN", "tutor-axios-error-without-response", {
        site: "marketplace",
        errorCode: error.code,
        errorName: error.name,
        errorMessage: error.message,
      });
      return;
    }

    handleApiResponseError(error, { ignoreExpected, notificationDetails, notify });
  };
};

/**
 * handleError for a lookup whose failure the page handles quietly (it falls back to what it has):
 * 401s still redirect to login and unexpected errors still log, but nothing is shown to the user.
 */
export const handleErrorWithoutNotification = () => handleError(false, undefined, () => undefined);

// Queries are fresh for 5 minutes and don't refetch on window focus. Queries whose server state
// changes with time or outside this cache (live sessions, payment status, polling) set
// `staleTime: 0` (or `refetchOnWindowFocus: "always"`) explicitly.
// makeMemberQuery defaults to `staleTime: Infinity`; fetchers migrated off axios pass this to keep
// the app's freshness window.
export const DEFAULT_STALE_TIME_MS = 1000 * 60 * 5;

const defaultOptions: DefaultOptions<unknown> = {
  queries: {
    onError: handleError(),
    retry: false,
    staleTime: DEFAULT_STALE_TIME_MS,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  },
};

// Queries whose network failure was reported with no data on screen, and that haven't fetched
// successfully since. Only a successful fetch removes the entry (setQueryData doesn't), so a query
// that loads and is later reset back to no data (resetQueries keeps the instance) reports again; a
// reset with no fetch success in between does not.
const reportedUnloadedQueries = new WeakSet<Query>();

const config: QueryClientConfig = {
  defaultOptions,
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Any failed refetch of data already on screen (polls, invalidations, remounts, initialData)
      // is left to the query's own error state. Reporting it would toast every poll interval while
      // offline; a mutation that triggered the invalidation reports its own failure. A query that
      // has never loaded reports its first network failure only, whatever failed before it, so a
      // poll that can't reach the server doesn't toast every tick either.
      if (query.state.data !== undefined || reportedUnloadedQueries.has(query)) return;
      if (reportFetcherNetworkFailure(error, query.meta)) reportedUnloadedQueries.add(query);
    },
    onSuccess: (_data, query) => {
      reportedUnloadedQueries.delete(query);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportFetcherNetworkFailure(error, mutation.options.meta);
    },
  }),
};

export default new QueryClient(config);
